import { Badge } from '@clickguard/design-system'
import {
	KeyboardEvent as ReactKeyboardEvent,
	useEffect,
	useRef,
	useState
} from 'react'

/** The box is a field, but not a DS `Input`. The fill is load-bearing: the criterion
 *  card behind it is grey. There is no `--cg-bg-primary` — an unknown var makes
 *  `rgb()` invalid and the declaration is dropped in silence. */
const BOX =
	'flex w-full items-center rounded-lg border border-solid border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] px-3 py-[7px]'
/** `focus-within` moves the ring out to the box, since the element that takes focus
 *  is a child. Spelled out because the DS ships its classes pre-compiled and carries
 *  only the `focus-visible:` forms, so a `focus-within:` one is inert and silent. */
const BOX_FOCUS =
	'focus-within:border-[rgb(var(--cg-brand))] focus-within:shadow-[0_0_0_2px_rgb(var(--cg-border-focus)/var(--cg-focus-ring-alpha,0.75))]'
/** `focus:ring-0` is not belt-and-braces on top of `outline-none`: this app loads
 *  `@tailwindcss/forms`, which gives every `[type="text"]:focus` its own blue ring
 *  through `--tw-ring-*`, and `outline-none` does not touch a box-shadow. */
const ENTRY =
	'min-w-0 flex-1 border-0 bg-transparent p-0 text-base leading-5 text-[rgb(var(--cg-text-primary))] outline-none focus:ring-0 placeholder:text-[rgb(var(--cg-text-tertiary))]'
/** `w-full` is what drops the chip row under the field: the criterion card is a
 *  wrapping flex row. */
const CHIPS = 'flex w-full flex-wrap items-center gap-1.5'

interface KeywordInputProps {
	values: string[]
	onChange: (values: string[]) => void
	placeholder?: string
	ariaLabel: string
	/** How many keywords the criterion can hold. The engine matches a scalar, so
	 *  `criteria.ts` caps this at 1 — the box locks once it is reached rather than
	 *  banking values the save would drop. */
	max?: number
	/** Border colours come from the caller's `surfaceFor`; only the focus ring is owned
	 *  here, because focus lands on a child. */
	className?: string
}

/** Several keywords in one criterion. It renders two siblings — the field, then the
 *  chip row — so the caller has to be a wrapping flex row. That is the criterion card. */
export const KeywordInput = ({
	values,
	onChange,
	placeholder,
	ariaLabel,
	className,
	max
}: KeywordInputProps) => {
	const [draft, setDraft] = useState('')
	const inputRef = useRef<HTMLInputElement>(null)

	// A keyword typed but never Entered is still one they meant, so commit on the way
	// out. `values` is read through a ref because this has to run on unmount only.
	const latest = useRef({ draft, values, full: false })
	useEffect(
		() => () => {
			const { draft: pending, values: banked, full } = latest.current
			const word = pending.trim()
			if (word && !full && !banked.includes(word)) onChange([...banked, word])
		},
		// Bare on purpose: this repo's `.eslintrc.json` never loads the
		// `react-hooks` plugin, so naming the rule is itself an error.
		// eslint-disable-next-line
		[]
	)

	const atMax = max !== undefined && values.length >= max
	latest.current = { draft, values, full: atMax }

	const commit = () => {
		const word = draft.trim()
		if (!word || atMax) return
		if (!values.includes(word)) onChange([...values, word])
		setDraft('')
	}

	const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			// Enter means "bank this word", never "submit the rule".
			event.preventDefault()
			commit()
			return
		}
		// Backspace at the start of an empty box eats the chip behind it.
		if (event.key === 'Backspace' && !draft && values.length) {
			onChange(values.slice(0, -1))
		}
	}

	return (
		<>
			<div
				className={`${BOX} ${BOX_FOCUS} ${className ?? ''}`}
				// The field's own padding is not the input, so a click there would do nothing.
				onMouseDown={(event) => {
					if (event.target !== event.currentTarget) return
					event.preventDefault()
					inputRef.current?.focus()
				}}
			>
				<input
					ref={inputRef}
					className={ENTRY}
					type="text"
					autoComplete="off"
					aria-label={ariaLabel}
					// Shown even with keywords banked: the box really is empty.
					placeholder={placeholder}
					value={atMax ? '' : draft}
					disabled={atMax}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={onKeyDown}
					onBlur={commit}
				/>
			</div>
			{values.length ? (
				<div className={CHIPS}>
					{values.map((value) => (
						<Badge
							key={value}
							variant="brand"
							size="sm"
							onClose={() =>
								onChange(values.filter((entry) => entry !== value))
							}
						>
							{value}
						</Badge>
					))}
				</div>
			) : null}
		</>
	)
}
